/**
 * Under `peterhof_agent`'s plan card: «В следующий раз» (#50) and the two
 * export buttons (#39). Everything here is the card's own data turned into
 * files on the device (`planExport.ts`) — no model, no request.
 *
 * Drawn only on the plan's NEWEST snapshot (`chat/TripCard.tsx`, in its sheet): an older
 * card under an earlier answer is a plan the visitor has since changed, and a
 * file saved from it would be the one copy they take to the park.
 */

import { useEffect, useState } from 'react';
import type { TripPlanProps } from '../catalog/generated/schemas';
import { useSession } from '../store/sessionStore';
import { saveFile } from './download';
import { exportFileName, planDocument, planIcs } from './planExport';
import { planLink } from './returnLink';

type PdfModule = typeof import('./planPdf');

let pdfModule: Promise<PdfModule> | null = null;

/** The PDF engine and its fonts, one lazy chunk — fetched once, then kept, so a
 *  plan saved later with no signal still saves. A failed fetch is forgotten,
 *  and the next tap tries again. */
function loadPdf(): Promise<PdfModule> {
  pdfModule ??= import('./planPdf').catch((error: unknown) => {
    pdfModule = null;
    throw error;
  });
  return pdfModule;
}

/** Fetch the PDF chunk in 1,5 s and → a cancel. Started by the plan's NEWEST card
 *  as it appears under the answer (`TripCard`), not by the sheet: a plan received
 *  online and first opened in the park with no signal must still save (Codex review
 *  of the rebase of !685 — the bar lives in the sheet, mounted only when it opens). */
export function preloadPdfSoon(): () => void {
  const timer = setTimeout(() => void loadPdf().catch(() => undefined), 1500);
  return () => clearTimeout(timer);
}

/** `preloadPdfSoon` while *enabled* (the plan's newest snapshot) is mounted. */
export function usePdfPreload(enabled: boolean): void {
  useEffect(() => (enabled ? preloadPdfSoon() : undefined), [enabled]);
}

/** Why a save did not happen — three different things for the visitor to do next. */
export type PdfFailure =
  /** No signal and the engine was never fetched: try again where there is some. */
  | 'offline'
  /** The page is OLDER than the site: its engine chunk (`planPdf-<hash>.js`) is gone
   *  from the server (every deploy renames it). Reload — the thread is kept. */
  | 'stale'
  /** The engine loaded and still failed — not a matter of signal. */
  | 'error';

/** Fetch the engine and save the file. A failed IMPORT with the network up is not «no
 *  signal»: the site was updated under an open tab, and only a reload fixes that. */
export async function savePlanPdf(
  props: TripPlanProps,
  now: Date,
  save: typeof saveFile = saveFile,
  link: string | null = planLink(props),
): Promise<PdfFailure | null> {
  let module: PdfModule;
  try {
    module = await loadPdf();
  } catch (error) {
    console.error('plan export: pdf chunk', error);
    return typeof navigator !== 'undefined' && navigator.onLine === false ? 'offline' : 'stale';
  }
  try {
    save(module.planPdf(planDocument(props, now, link)), exportFileName(props, 'pdf'), 'application/pdf');
    return null;
  } catch (error) {
    console.error('plan export: pdf', error);
    return 'error';
  }
}

const FAILURE_TEXT: Record<PdfFailure, string> = {
  offline: 'Не получилось сохранить PDF — попробуйте ещё раз, когда будет связь.',
  stale: 'Гид обновился — обновите страницу, и PDF сохранится. Ваш план останется на месте.',
  error: 'Не получилось сохранить PDF — попробуйте ещё раз.',
};

export function NextVisit({ items }: { items: TripPlanProps['next_visit'] }) {
  if (!items || items.length === 0) return null;
  return (
    <details className="plan__next">
      <summary>В следующий раз</summary>
      <ul className="plan__next-list">
        {items.map((item) => (
          <li key={item.title} className="plan__next-item">
            <span className="plan__next-title">{item.title}</span>
            {item.season ? <span className="plan__next-season"> · {item.season}</span> : null}
            {item.summary ? <div className="plan__next-summary">{item.summary}</div> : null}
          </li>
        ))}
      </ul>
    </details>
  );
}

export default function PlanSaveBar({ props }: { props: TripPlanProps }) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<PdfFailure | null>(null);
  const hasDates = (props.dates ?? []).length > 0;

  const savePdf = async () => {
    setBusy(true);
    setFailed(null);
    setFailed(await savePlanPdf(props, new Date()));
    setBusy(false);
  };

  const saveIcs = () => {
    const ics = planIcs(props, new Date(), useSession.getState().threadId, planLink(props));
    if (ics) saveFile(ics, exportFileName(props, 'ics'), 'text/calendar;charset=utf-8');
  };

  return (
    // `data-sheet-stays`: a save sends nothing, so the plan's sheet stays open (`TripCard`).
    <div className="plan__export" data-sheet-stays>
      <button type="button" className="chip" onClick={savePdf} disabled={busy}>
        {busy ? 'Готовлю PDF…' : 'Скачать PDF'}
      </button>
      {hasDates ? (
        <button type="button" className="chip" onClick={saveIcs}>
          В календарь
        </button>
      ) : (
        <span className="plan__export-hint">В календарь — когда будет выбрана дата визита</span>
      )}
      {failed ? (
        <p className="plan__export-error" role="status">
          {FAILURE_TEXT[failed]}
          {failed === 'stale' ? (
            <>
              {' '}
              <button type="button" className="chip" onClick={() => window.location.reload()}>
                Обновить страницу
              </button>
            </>
          ) : null}
        </p>
      ) : null}
    </div>
  );
}
